import { ReactNode, useState } from "react"
import { useTranslation } from "react-i18next"
import { ScheduledWorkflow } from "@/reducers/openclawWorkflowsSlice"
import { ChatAutomationScope } from "@/lib/chatAutomations"
import { useRoomWorkflows } from "@/hooks/useRoomWorkflows"
import { StopAutomationEditDialog } from "@/components/routeStops/StopAutomationEditDialog"
import { CronAutomationDialog } from "./CronAutomationDialog"
import { AutomationDetailsDialog } from "./AutomationDetailsDialog"
import FleetReminderSection from "./FleetReminderSection"
import {
  isEditable,
  isScheduleRule,
  isStopRule,
  AddStopAutomationLink,
  TransportBriefRow,
  WorkflowItem,
  sortActiveFirst,
} from "./workflowRows"

/** One bordered section of the room panel — Andrzej's prototype puts each
 * group (Briefing, Shared, Fleet Reminders, Personal) in its own card
 * instead of the plain stacked headings the account-wide list uses. */
const SectionCard = ({
  label,
  children,
}: {
  label: string
  children: ReactNode
}) => (
  <section className="rounded-lg border border-border p-3">
    <p className="mb-2 text-xs font-medium text-muted-foreground">{label}</p>
    {children}
  </section>
)

/**
 * The automations of one driver room, grouped by who each rule is about:
 * the fleet-wide brief and rules first, then this truck's reminders, then
 * the rules and create-shortcuts about this driver. Rules aimed at another
 * truck never reach here.
 *
 * The account-wide panel is `WorkflowList`; it groups by who owns a rule
 * instead, which is the question that matters when every truck is in view.
 */
const RoomAutomations = ({
  scope,
  onAddStopAutomation,
}: {
  scope: ChatAutomationScope
  /** The room always has a driver to attach a new rule to, so the shortcut
   * is part of the surface, not an option. */
  onAddStopAutomation: () => void
}) => {
  const { t } = useTranslation()
  const { forThisDriver, fleetWide, fleetReminders, isMine } =
    useRoomWorkflows(scope)
  const [editing, setEditing] = useState<ScheduledWorkflow | null>(null)
  const [viewing, setViewing] = useState<ScheduledWorkflow | null>(null)

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div className="flex flex-1 flex-col gap-3 overflow-y-auto p-4">
        {/* The transport brief is written for the whole fleet, so it gets its
            own card — the same daily digest the account-wide panel's
            BriefingSection links to. */}
        <SectionCard label={t("ai.automations.sections.briefing")}>
          <TransportBriefRow subtitle={t("ai.automations.dailyDigest")} />
        </SectionCard>

        {/* Rows here stay plain on purpose: editing a fleet-wide rule from one
            driver's room would silently change every other driver too — the
            same reason a stop's menu refuses to edit one. */}
        <SectionCard label={t("ai.automations.sections.shared")}>
          <div className="flex flex-col">
            {(sortActiveFirst(fleetWide) as ScheduledWorkflow[]).map((job) => (
              <WorkflowItem key={job.id} job={job} layout="stacked" />
            ))}
          </div>
        </SectionCard>

        {fleetReminders.length > 0 && (
          <SectionCard label={t("ai.automations.sections.fleetReminders")}>
            <div className="flex flex-col gap-3">
              {fleetReminders.map((reminder) => (
                <FleetReminderSection key={reminder.id} reminder={reminder} />
              ))}
            </div>
          </SectionCard>
        )}

        <SectionCard label={t("ai.automations.sections.personal")}>
          {forThisDriver.length > 0 && (
            <div className="flex flex-col">
              {(sortActiveFirst(forThisDriver) as ScheduledWorkflow[]).map(
                (job) => (
                  <WorkflowItem
                    key={job.id}
                    job={job}
                    layout="stacked"
                    // Someone else's rule is read-only anyway — the agent
                    // rejects a write from a non-owner. Mine opens its form,
                    // or a details view for the kinds no form can express.
                    onEdit={
                      !isMine(job)
                        ? undefined
                        : isEditable(job)
                          ? setEditing
                          : setViewing
                    }
                  />
                ),
              )}
            </div>
          )}
          {forThisDriver.length === 0 && (
            <p className="py-1 text-sm text-muted-foreground">
              {t("ai.automations.nothingForDriver")}
            </p>
          )}
          <AddStopAutomationLink
            onAddStopAutomation={onAddStopAutomation}
            jobs={forThisDriver}
          />
        </SectionCard>
      </div>

      <StopAutomationEditDialog
        job={editing && isStopRule(editing) ? editing : null}
        onOpenChange={(open) => !open && setEditing(null)}
      />
      <CronAutomationDialog
        job={editing && isScheduleRule(editing) ? editing : null}
        onOpenChange={(open) => !open && setEditing(null)}
      />
      <AutomationDetailsDialog
        job={viewing}
        onOpenChange={(open) => !open && setViewing(null)}
      />
    </div>
  )
}

export default RoomAutomations
